import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { clientPortalService } from '../../services/clientPortalService';
import { openaiService } from '../../services/openaiService';
import AIAnalysisSummary from './components/AIAnalysisSummary';
import RecommendationCard from './components/RecommendationCard';
import AIInsightsPanel from './components/AIInsightsPanel';

const AIPoweredServiceRecommendations = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [roiScenarios, setRoiScenarios] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    loadInitialData();
  }, [user, navigate]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const data = await clientPortalService?.getClientDashboardSummary(user?.id);
      setDashboardData(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const generateAIRecommendations = async () => {
    try {
      setGenerating(true);
      setError('');

      const clientData = {
        company: userProfile?.company,
        currentMaturityScore: dashboardData?.currentMaturityScore,
        dataInfrastructure: dashboardData?.latestScores?.data_infrastructure?.score || 0,
        analyticsCapabilities: dashboardData?.latestScores?.analytics_capabilities?.score || 0,
        governanceMaturity: dashboardData?.latestScores?.governance_maturity?.score || 0,
        teamSkills: dashboardData?.latestScores?.team_skills?.score || 0,
        strategicAlignment: dashboardData?.latestScores?.strategic_alignment?.score || 0,
        consultationCount: dashboardData?.totalConsultations,
        activeRecommendations: dashboardData?.activeRecommendations,
        industry: userProfile?.industry || 'Technology'
      };

      const [analysis, aiRecs] = await Promise.all([
        openaiService?.analyzeClientMaturity(clientData),
        openaiService?.generateServiceRecommendations(clientData)
      ]);

      setAiAnalysis(analysis);
      setRecommendations(aiRecs?.recommendations || []);
    } catch (err) {
      setError(err?.message || 'Failed to generate AI recommendations');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewROIScenarios = async (recommendation) => {
    try {
      setSelectedRecommendation(recommendation);
      setGenerating(true);

      const clientData = {
        currentMaturityScore: dashboardData?.currentMaturityScore,
        company: userProfile?.company
      };

      const scenarios = await openaiService?.generateROIScenarios(recommendation, clientData);
      setRoiScenarios(scenarios);
    } catch (err) {
      setError(err?.message || 'Failed to generate ROI scenarios');
    } finally {
      setGenerating(false);
    }
  };

  const handleCloseScenarios = () => {
    setSelectedRecommendation(null);
    setRoiScenarios(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-[#1FB5A5] mx-auto" />
            <p className="mt-4 text-white">Loading client data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !recommendations?.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto" />
            <p className="mt-4 text-red-400">{error}</p>
            <Button onClick={loadInitialData} className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>AI-Powered Service Recommendations | Northstar Data Strategy</title>
        <meta
          name="description"
          content="Get personalized service recommendations with AI-driven ROI predictions based on your data maturity profile."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />

        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Hero Section */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-[#1FB5A5]/20 to-[#002B45]/20 backdrop-blur-sm rounded-xl p-8 border border-[#1FB5A5]/30">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-[#1FB5A5] rounded-full flex items-center justify-center">
                        <Icon name="Sparkles" size={24} className="text-white" />
                      </div>
                      <div>
                        <h1 className="text-3xl font-bold text-white">
                          AI-Powered Service Recommendations
                        </h1>
                        <p className="text-[#1FB5A5] text-lg font-medium">Intelligent ROI Prediction Center</p>
                      </div>
                    </div>
                    <p className="text-gray-300 max-w-3xl">
                      Leverage advanced AI analysis to receive personalized service recommendations with sophisticated ROI predictions tailored to your organization's data maturity profile and strategic objectives.
                    </p>
                  </div>
                  <Button
                    onClick={generateAIRecommendations}
                    disabled={generating}
                    iconName={generating ? 'Loader2' : 'Sparkles'}
                    iconPosition="left"
                    size="lg"
                    className="bg-[#1FB5A5] hover:bg-[#1FB5A5]/90"
                  >
                    {generating ? 'Generating...' : recommendations?.length > 0 ? 'Regenerate' : 'Generate Recommendations'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && recommendations?.length > 0 && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-800">
                  <Icon name="AlertCircle" size={20} />
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* AI Analysis Summary */}
            {aiAnalysis && (
              <AIAnalysisSummary
                analysis={aiAnalysis}
                currentScore={dashboardData?.currentMaturityScore}
                latestScores={dashboardData?.latestScores}
              />
            )}

            {/* Recommendations Grid */}
            {recommendations?.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Prioritized Service Recommendations</h2>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Icon name="Lightbulb" size={20} className="text-[#1FB5A5]" />
                    <span className="text-sm">{recommendations?.length} recommendations generated</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {recommendations?.map((recommendation, index) => (
                    <RecommendationCard
                      key={index}
                      recommendation={recommendation}
                      rank={index + 1}
                      onViewROI={() => handleViewROIScenarios(recommendation)}
                      isGenerating={generating && selectedRecommendation === recommendation}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* AI Insights Panel */}
            {recommendations?.length > 0 && (
              <AIInsightsPanel
                recommendations={recommendations}
                analysis={aiAnalysis}
                dashboardData={dashboardData}
              />
            )}

            {/* Empty State */}
            {!recommendations?.length && !generating && (
              <div className="bg-white rounded-xl shadow-elevation-2 p-12 text-center">
                <div className="w-20 h-20 bg-[#1FB5A5]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Sparkles" size={40} className="text-[#1FB5A5]" />
                </div>
                <h3 className="text-2xl font-bold text-[#002B45] mb-3">
                  Ready to Discover Your Opportunities?
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Click "Generate Recommendations" to receive AI-powered service suggestions with detailed ROI predictions based on your current data maturity profile and organizational context.
                </p>
                <Button
                  onClick={generateAIRecommendations}
                  iconName="Sparkles"
                  iconPosition="left"
                  size="lg"
                  className="bg-[#1FB5A5] hover:bg-[#1FB5A5]/90"
                >
                  Generate Recommendations
                </Button>
              </div>
            )}

            {/* ROI Scenarios Modal */}
            {selectedRecommendation && roiScenarios && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-elevation-2 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-[#002B45]">{selectedRecommendation?.serviceName}</h3>
                      <p className="text-sm text-gray-500">ROI Scenario Analysis</p>
                    </div>
                    <button
                      onClick={handleCloseScenarios}
                      className="w-10 h-10 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                    >
                      <Icon name="X" size={20} className="text-gray-500" />
                    </button>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Conservative Scenario */}
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Icon name="TrendingDown" size={20} className="text-orange-600" />
                          <h4 className="font-bold text-orange-900">Conservative</h4>
                        </div>
                        <div className="text-3xl font-bold text-orange-600 mb-4">
                          {roiScenarios?.conservative?.roi}%
                        </div>
                        <p className="text-sm text-orange-700 mb-4">
                          Timeline: {roiScenarios?.conservative?.timelineMonths} months
                        </p>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-orange-900 mb-2">Assumptions:</p>
                            <ul className="space-y-1">
                              {roiScenarios?.conservative?.assumptions?.map((assumption, idx) => (
                                <li key={idx} className="text-xs text-orange-700 flex items-start gap-1">
                                  <Icon name="Circle" size={8} className="mt-1 flex-shrink-0" />
                                  <span>{assumption}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-orange-900 mb-2">Success Factors:</p>
                            <ul className="space-y-1">
                              {roiScenarios?.conservative?.successFactors?.map((factor, idx) => (
                                <li key={idx} className="text-xs text-orange-700 flex items-start gap-1">
                                  <Icon name="CheckCircle" size={12} className="mt-0.5 flex-shrink-0" />
                                  <span>{factor}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Expected Scenario */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Icon name="TrendingUp" size={20} className="text-blue-600" />
                          <h4 className="font-bold text-blue-900">Expected</h4>
                        </div>
                        <div className="text-3xl font-bold text-blue-600 mb-4">
                          {roiScenarios?.expected?.roi}%
                        </div>
                        <p className="text-sm text-blue-700 mb-4">
                          Timeline: {roiScenarios?.expected?.timelineMonths} months
                        </p>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-blue-900 mb-2">Assumptions:</p>
                            <ul className="space-y-1">
                              {roiScenarios?.expected?.assumptions?.map((assumption, idx) => (
                                <li key={idx} className="text-xs text-blue-700 flex items-start gap-1">
                                  <Icon name="Circle" size={8} className="mt-1 flex-shrink-0" />
                                  <span>{assumption}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-blue-900 mb-2">Success Factors:</p>
                            <ul className="space-y-1">
                              {roiScenarios?.expected?.successFactors?.map((factor, idx) => (
                                <li key={idx} className="text-xs text-blue-700 flex items-start gap-1">
                                  <Icon name="CheckCircle" size={12} className="mt-0.5 flex-shrink-0" />
                                  <span>{factor}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Optimistic Scenario */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Icon name="TrendingUp" size={20} className="text-green-600" />
                          <h4 className="font-bold text-green-900">Optimistic</h4>
                        </div>
                        <div className="text-3xl font-bold text-green-600 mb-4">
                          {roiScenarios?.optimistic?.roi}%
                        </div>
                        <p className="text-sm text-green-700 mb-4">
                          Timeline: {roiScenarios?.optimistic?.timelineMonths} months
                        </p>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-green-900 mb-2">Assumptions:</p>
                            <ul className="space-y-1">
                              {roiScenarios?.optimistic?.assumptions?.map((assumption, idx) => (
                                <li key={idx} className="text-xs text-green-700 flex items-start gap-1">
                                  <Icon name="Circle" size={8} className="mt-1 flex-shrink-0" />
                                  <span>{assumption}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-green-900 mb-2">Success Factors:</p>
                            <ul className="space-y-1">
                              {roiScenarios?.optimistic?.successFactors?.map((factor, idx) => (
                                <li key={idx} className="text-xs text-green-700 flex items-start gap-1">
                                  <Icon name="CheckCircle" size={12} className="mt-0.5 flex-shrink-0" />
                                  <span>{factor}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AIPoweredServiceRecommendations;